/**
 * Step 04 — the logo (spec §2.2, §2.5).
 *
 * Nobody is asked to produce a logo they don't have: the brand's NAME is
 * already drawn as a wordmark and already on the phone by the time this screen
 * opens (the shell owns that, so a later colour or font change redraws it).
 * This screen only offers to replace it — which is why Continue is always
 * live here, and why the old "Continue is disabled, no reason given" defect
 * cannot come back.
 */
import { useState } from "react";
import { Upload } from "lucide-react";
import type { BrandSetupState } from "../../../lib/brandSetup/state";
import { MONO_LABEL, REASON, ReelTag } from "../ui";

export function LogoStep(props: {
  state: BrandSetupState;
  /** File name of an uploaded logo, so the row can name what is on screen. */
  fileName: string | null;
  /** A rejected file's sentence (type / size / unreadable). */
  error: string | null;
  /** A restored draft whose uploaded bytes are gone (spec §8). */
  note: string | null;
  /** The study saw a logo on the reel and the placeholder is in its spot
   *  (`studyFoundLogo(state)`, round-2 §2.3). */
  studyFoundLogo: boolean;
  /** "Use ours instead" on the reel tag: every logo chip is reset, the
   *  placeholder leaves the reel's spot and the wordmark takes over. */
  onResetReelLogo: () => void;
  onFile: (file: File) => void;
}): React.JSX.Element {
  const { state, fileName, error, note, studyFoundLogo } = props;
  const [dropping, setDropping] = useState(false);
  const uploaded = state.logoKind === "upload" && !!state.logoDataUrl;
  const placeholderShown = studyFoundLogo && !uploaded;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {placeholderShown && <ReelTag label="From your reel" onReset={props.onResetReelLogo} />}
      {/* "Replace" is only honest while something IS on the video — if the
          wordmark could not be drawn at all, this step is an ask, not a swap.
          With a studied logo the thing on the video is the placeholder, and
          the label says what the upload does to it — unless the wordmark has
          already been drawn (the operator passed this step and came back), in
          which case the wordmark IS what is on the video. */}
      <div style={MONO_LABEL}>
        {uploaded
          ? "Your logo"
          : placeholderShown && state.logoKind !== "wordmark"
            ? "Add your logo — it takes the placeholder's spot"
            : state.logoDataUrl
              ? "Replace with your logo"
              : "Add your logo"}
      </div>
      <label
        htmlFor="bs-logo"
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => {
          e.preventDefault();
          setDropping(true);
        }}
        onDragLeave={() => setDropping(false)}
        onDrop={(e) => {
          // preventDefault FIRST: a dropped image the browser opens itself
          // navigates the SPA away and the whole wizard is gone.
          e.preventDefault();
          setDropping(false);
          const file = e.dataTransfer.files[0];
          if (file) props.onFile(file);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          minHeight: 96,
          padding: 14,
          borderRadius: 16,
          border: dropping
            ? "2px dashed var(--ql-yellow)"
            : uploaded
              ? "1px solid rgba(255,255,255,.10)"
              : "2px dashed rgba(255,255,255,.14)",
          background: dropping ? "rgba(255,213,0,.06)" : "rgba(255,255,255,.02)",
          cursor: "pointer",
          color: dropping ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
          fontSize: 13,
          transition: "all .15s var(--ql-ease)",
        }}
      >
        {uploaded ? (
          <>
            <img
              src={state.logoDataUrl}
              alt="Your logo"
              style={{ maxWidth: 120, maxHeight: 60, objectFit: "contain", flexShrink: 0 }}
            />
            <span
              style={{
                flex: 1,
                minWidth: 0,
                color: "var(--ql-fg)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {fileName ?? "Your logo"}
            </span>
            <span style={{ color: "var(--ql-yellow)", fontWeight: 700, flexShrink: 0 }}>Replace</span>
          </>
        ) : (
          <>
            <Upload size={18} />
            <span>Drop your logo here, or click — PNG, JPG or WebP, up to 4 MB</span>
          </>
        )}
        <input
          id="bs-logo"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) props.onFile(f);
            // Clear it either way: after a rejected file the input still holds
            // that name, so re-picking the SAME corrected file fires no change
            // event and the user sees nothing happen.
            e.target.value = "";
          }}
        />
      </label>
      {note && <div style={{ fontSize: 13, color: "var(--ql-fg-muted)", lineHeight: 1.55 }}>{note}</div>}
      {error && <div style={REASON}>{error}</div>}
    </div>
  );
}
